import { type AccountSummary, formatDecimal } from "@trader/shared/Money"
import { Result } from "effect"
import type { Timeframe } from "@trader/shared/MarketData"
import { useEffect, useMemo, useState } from "react"
import { call } from "../../api.ts"
import { attachRequest, paperTradeTarget } from "../strategies/attachRunner.ts"
import { strategyInfoFor, usePaneBacktest } from "../strategies/strategyResults.ts"
import { buildCreateAccount, defaultForm, type NewAccountErrors, type NewAccountForm } from "../trading/newAccount.ts"
import { messageFor, tradingApi } from "../trading/tradingApi.ts"
import { accountsStore, syncChartPreference, useAccounts } from "../trading/tradingData.ts"
import type { PickedBar } from "./replaySession.ts"
import { replayAccountName, replayStartTime, replayStep, reusableAccounts, syncedProperties } from "./replayStart.ts"

export type ReplayAccountChoice = "new" | "existing"

/** A strategy's initial capital: the chart's override, else the strategy's own default (its last run). */
const capitalOf = (properties: Readonly<Record<string, unknown>>, defaults: { readonly initialCapital?: unknown } | undefined): number | null => {
  const value = properties["initialCapital"] ?? defaults?.initialCapital
  return typeof value === "number" && Number.isFinite(value) && value > 0 ? value : null
}

/**
 * The "Replay from this bar" dialog's state and its Start (docs/design/0007): a
 * new Replay account from the bar, or an existing one moved forward to it; then
 * the chart's strategy, if asked, paper trades it from that bar; the charts follow
 * the account's clock.
 */
export const useReplayStart = (picked: PickedBar) => {
  const { barTime, instrument, strategy, timeframe } = picked
  const backtest = usePaneBacktest(picked.pane)
  // In sync, the backtest and the account size orders from the same money: a new
  // account starts with the strategy's capital (docs/design/0007).
  const defaults = strategy === null ? undefined : strategyInfoFor(backtest, strategy.id)?.defaultProperties
  const capital = strategy === null ? null : capitalOf(strategy.properties, defaults)
  const start = replayStartTime(instrument, timeframe, barTime)
  // One-minute steps when the symbol has 1m bars (docs/design/0011); until that is known, assume it does.
  const [step, setStep] = useState<Timeframe>(replayStep(timeframe, true))
  useEffect(() => {
    let current = true
    call((client) => client.candles.latest({ query: { instrument: instrument.id, timeframe: "1m" } })).then(
      // 1m bars must reach past the start, or the clock would have nothing to step through.
      ({ time }) => current && setStep(replayStep(timeframe, time !== null && time >= start)),
      () => undefined
    )
    return () => {
      current = false
    }
  }, [instrument.id, timeframe, start])
  const accounts = useAccounts()
  const reusable = useMemo(
    () => reusableAccounts(accounts.accounts, instrument, step, start),
    [accounts.accounts, instrument, step, start]
  )
  const [choice, setChoice] = useState<ReplayAccountChoice>("new")
  const [existingId, setExistingId] = useState<string>("")
  const [form, setForm] = useState<NewAccountForm>(() => {
    const defaults = defaultForm(instrument.currency, replayStep(timeframe, true), new Date())
    return {
      ...defaults,
      name: replayAccountName(instrument, timeframe, start),
      startingCash: capital !== null ? String(capital) : defaults.startingCash
    }
  })
  const target = strategy === null ? null : paperTradeTarget(strategy.id, null, null)
  const canTrade = target?._tag === "Ready"
  const [trade, setTrade] = useState(canTrade)
  const [errors, setErrors] = useState<NewAccountErrors>({})
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  /** An account this dialog created whose strategy didn't attach: Start retries on it. */
  const [created, setCreated] = useState<AccountSummary | null>(null)

  // The account steps `step` bars, whatever the chart shows.
  useEffect(() => setForm((current) => (current.timeframe === step ? current : { ...current, timeframe: step })), [step])

  const existing = reusable.find((account) => account.id === existingId) ?? reusable[0]

  const submit = async (): Promise<boolean> => {
    setError(null)
    let account: AccountSummary
    if (created !== null) account = created
    else if (choice === "existing") {
      if (existing === undefined) return false
      account = existing
    } else {
      const check = buildCreateAccount(form, Math.floor(Date.now() / 1000), start)
      if (!check.ok) {
        setErrors(check.errors)
        return false
      }
      setErrors({})
      setBusy(true)
      try {
        account = await accountsStore.create(check.request)
      } catch (failure) {
        setBusy(false)
        setError(messageFor(failure).detail)
        return false
      }
      setCreated(account)
    }
    setBusy(true)
    try {
      accountsStore.select(account.id)
      // Bring an existing account to the bar first, so the strategy starts trading there, not on the way.
      if (account.clock._tag === "Replay" && account.clock.now < start) {
        await tradingApi.clock(account.id, { _tag: "Advance", to: start })
      }
      if (trade && strategy !== null && target?._tag === "Ready") {
        // The account's starting cash is the strategy's capital, on the server's run and on the chart's.
        const cash = Number(formatDecimal(account.startingCash))
        const { properties } = syncedProperties(strategy.properties, defaults, cash, account.config.chargeModel, start)
        const request = attachRequest({
          strategyId: target.strategyId,
          instrument,
          product: "Cash",
          startPolicy: "waitForFlat",
          direction: "long",
          inputs: strategy.inputs,
          properties,
          // The chart's bars, though the clock steps one minute (docs/design/0011).
          timeframe
        })
        if (Result.isFailure(request)) {
          setError(request.failure)
          return false
        }
        // The chart's backtest follows the runner while the charts follow this replay (ChartPane, useSyncedRunner).
        await call((client) => client.accounts.attachRunner({ params: { id: account.id }, payload: request.success }))
      }
      syncChartPreference.set("on")
      return true
    } catch (failure) {
      setError(messageFor(failure).detail)
      return false
    } finally {
      setBusy(false)
    }
  }

  return {
    start,
    step,
    reusable,
    choice: created !== null ? "new" : choice,
    setChoice,
    existing,
    setExistingId,
    form,
    setForm,
    errors,
    strategy,
    capital,
    /** The orders would be sized down to 95% of equity (charges), for the dialog to say so. */
    sizedDown: strategy !== null &&
      syncedProperties(strategy.properties, defaults, 0, choice === "new" ? { _tag: form.charges === "Zero" ? "Zero" : "FyersNseEquity" } : existing?.config.chargeModel ?? { _tag: "Zero" }, start).sizedDown,
    canTrade,
    tradeNote: target !== null && target._tag !== "Ready" ? target.message : null,
    trade: canTrade && trade,
    setTrade,
    error,
    busy,
    created,
    submit
  }
}
